import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import cx from 'classnames'
import FloatingActionButton from 'material-ui/FloatingActionButton'
import ContentAdd from 'material-ui/svg-icons/content/add'
import MessagesEditor from '../../Messages/MessagesEditor'
import { getPatientMessages } from '../../../state/actions/selected-patient'
import { chooseCurrentConversation } from '../../../state/actions/current'
import {
  startNewConversation,
  addMediaToMessage,
} from '../../../state/actions/conversations-messages'
import { renderAlert } from '../../../services/render-alert'

import css from './converse.css'

export default (WrappedComponent, { messagesType, displayName }) => {
  class Converse extends Component {
    static propTypes = {
      handleAddMedia: PropTypes.func.isRequired,
      attachedMedia: PropTypes.string,
      creatingNewConversation: PropTypes.bool,
      conversationById: PropTypes.objectOf(
        PropTypes.shape({
          active: PropTypes.bool,
          created_at: PropTypes.string,
          id: PropTypes.number,
          patients: PropTypes.arrayOf(
            PropTypes.shape({
              first_name: PropTypes.string,
              last_name: PropTypes.string,
            }),
          ),
          seen: PropTypes.bool,
          subject: PropTypes.string,
          updated_at: PropTypes.string,
        }),
      ),
      countryCode: PropTypes.string,
      currentConversationId: PropTypes.number,
      handleSelectConversation: PropTypes.func.isRequired,
      handleStartNewConversation: PropTypes.func.isRequired,
      hasRemindersTier2: PropTypes.bool.isRequired,
      getData: PropTypes.func.isRequired,
      isEmail: PropTypes.bool.isRequired,
      isFetching: PropTypes.bool.isRequired,
      patientId: PropTypes.string.isRequired,
      patientConversations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
        }),
      ).isRequired,
      patientMessages: PropTypes.arrayOf(
        PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
          }),
        ),
      ).isRequired,
    }

    static displayName = `Converse${
      displayName || Converse.name || 'Component'
    }`

    state = {
      isEditorFocused: false,
      selectedMessageId: false, // for emails only, identify which message to focus on.
    }

    componentDidMount() {
      // if (!this.props.isFetching && this.props.conversationIds.length === 0 && this.props.patientId) {
      if (true) {
        // TODO: think of better way to fetch upon tab click
        this.fetchMessages()
      }
    }

    componentDidUpdate(prevProps) {
      if (this.props.patientId !== prevProps.patientId) {
        this.fetchMessages()
      }
    }

    fetchMessages = () => {
      const { getData, patientConversations, patientId } = this.props

      return getData(patientId, messagesType).then(() => {
        const len = patientConversations.length

        if (len > 0) {
          this.chooseCurrentConversation(patientConversations[len - 1].id)
        }

        return Promise.resolve()
      })
    }

    toggleEditorFocus = () => {
      this.setState({ isEditorFocused: !this.state.isEditorFocused })
    }

    handleMessageFocus = val => {
      // pass in `false` or something non-numerical to "unselect" something
      this.setState({ selectedMessageId: val })
    }

    chooseCurrentConversation = nextId => {
      if (this.props.currentConversationId !== nextId) {
        this.props.handleSelectConversation(nextId)
      }
    }

    startNewConvo = async () => {
      const { handleStartNewConversation, patientId } = this.props
      try {
        handleStartNewConversation(messagesType, patientId)
      } catch (err) {
        renderAlert(err, 'error')
      }
    }

    handlePendingMediaPreview = media => {
      const { handleAddMedia, currentConversationId } = this.props
      handleAddMedia(media, currentConversationId)
    }

    render() {
      const {
        attachedMedia,
        creatingNewConversation,
        conversationById,
        countryCode,
        currentConversationId,
        hasRemindersTier2,
        isEmail,
        isFetching,
        messageSettingsById,
        patientConversations,
        patientMessages,
        templateSettings,
      } = this.props

      const { isEditorFocused, selectedMessageId } = this.state

      if (isFetching) {
        return <h2>Loading Messages...</h2>
      }

      const foundPatientConversation = patientConversations.length > 0
      // ensures current selected conversation matches the selected view
      const correctConvoType = isEmail
        ? messagesType === 'email'
        : messagesType === 'text'
      const validConversation =
        correctConvoType && (currentConversationId || creatingNewConversation)
      const wrappedComponentCss = cx(css.wrappedComponent, {
        [css.email]: isEmail,
      })
      const editorCss = cx(css.editor, { [css.email]: isEmail })

      return (
        <div className={css.container}>
          {foundPatientConversation ? (
            <div className={wrappedComponentCss}>
              <WrappedComponent
                attachedMedia={attachedMedia}
                conversationById={conversationById}
                countryCode={countryCode}
                currentConversationId={currentConversationId}
                hasRemindersTier2={hasRemindersTier2}
                handleMessageFocus={this.handleMessageFocus}
                handleChooseCurrentConversation={this.chooseCurrentConversation}
                isEmail={isEmail}
                patientConversations={patientConversations}
                patientMessages={patientMessages}
                previewPendingMedia={this.handlePendingMediaPreview}
                selectedMessageId={selectedMessageId}
              />
            </div>
          ) : (
            <span className={css.noneFound}>no messages found</span>
          )}
          {validConversation ? (
            <div className={editorCss}>
              <MessagesEditor
                isFocused={isEditorFocused}
                toggleFocus={this.toggleEditorFocus}
                previewPendingMedia={this.handlePendingMediaPreview}
                hasRemindersTier2={hasRemindersTier2}
                messageSettingsById={messageSettingsById}
                templateSettings={templateSettings}
              />
            </div>
          ) : (
            <FloatingActionButton
              backgroundColor="#6040B0"
              className={css.newConvoButton}
              onClick={this.startNewConvo}
            >
              <ContentAdd />
            </FloatingActionButton>
          )}
        </div>
      )
    }
  }

  const mapStateToProps = ({
    account: {
      auth: { hasRemindersTier2 },
    },
    selectedPatient: {
      messages,
      messages: { isFetching },
      location: { countryCode },
    },
    messages: { dataByConversation, byId: messagesById },
    current: {
      conversation: { id: currentConversationId, isEmail },
    },
    conversations: { byId: conversationsById, creatingNewConversation },
  }) => {
    const convosIdKey = `${messagesType}ConversationIds`
    const conversationIds = messages[convosIdKey]
    const patientMessages = []
    const patientConversations = []
    const attachedMedia = dataByConversation.hasOwnProperty(
      currentConversationId,
    )
      ? dataByConversation[currentConversationId].attachedMedia
      : null

    conversationIds.forEach(convoId => {
      const convo = convoId && conversationsById[convoId]
      const messageIds =
        dataByConversation[convoId] && dataByConversation[convoId].messageIds

      if (convo && messageIds && convo.messages_type === messagesType) {
        patientMessages.push(messageIds.map(msgId => messagesById[msgId]))
        patientConversations.push(convo)
      }
    })

    return {
      attachedMedia,
      conversationById: conversationsById,
      countryCode,
      currentConversationId,
      hasRemindersTier2,
      isFetching,
      patientMessages,
      patientConversations,
      isEmail,
      creatingNewConversation,
    }
  }

  const mapDispatchToProps = dispatch =>
    bindActionCreators(
      {
        getData: getPatientMessages,
        handleSelectConversation: chooseCurrentConversation,
        handleStartNewConversation: startNewConversation,
        handleAddMedia: addMediaToMessage,
      },
      dispatch,
    )

  return connect(mapStateToProps, mapDispatchToProps)(Converse)
}



// WEBPACK FOOTER //
// ./src/components/PatientPage/Messages/converse.js